import { DragIcon, EditIcon, EyeIcon } from "~/assets/icons";
import styles from "./EstimateStep4.module.scss";
import classNames from "classnames/bind";
import { useEffect, useState } from "react";
import FormToggle from "~/components/common/form/formToggle/FormToggle";
import BaseSelectCustom from "~/components/common/base-select-custom/BaseSelectCustom";
import FormInput from "~/components/common/form/formInput/FormInput";
import FormNumber from "~/components/common/form/formNumber/FormNumber";
import { PlusIcon, Trash2, TrashIcon } from "lucide-react";
import { EstimateModeViewEnum, EstimateUoMOption } from "~/pages/admin/jobs/add-job/constants/add-estimate.constants";
import AddItem from "~/components/common/add-item/AddItem";
import DroppableArea from "~/components/common/drop-drag/DroppableArea";
import TitleEditor from "~/components/common/form/titleEdit/TitleEditor";
import BaseBoxContent from "~/components/common/base-box-content/BaseBoxContent";
import { Divider } from "@mui/material";

const cx = classNames.bind(styles);

const MAX_UNIT_PRICE = 999_999_999.99;
const MAX_QUANTITY = 999_999_999;

const formatMoney = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

interface EstimateStep4Props {
  onChange: (formData:any) => void,
  data:any,
  mode:string,
  errorForm:any,
  onPreview: () => void,
}


interface sectionItem {
    itemName: string,
    type:string,  // "MATERIAL",
    uom: string,  //"SQ_FT",
    quantity: number,
    unitPrice: number
} 
interface section {
    title: string,
    isVisible: boolean,
    sectionItems:sectionItem[]
} 

interface quote {
    quoteName:string,
    sections:section[],
    quoteProfitMargin: any,
    note: string,
}

const DEFAULT_SECTION: section = {
    title: "Section 1",
    isVisible: true,
    sectionItems: [{
        itemName: "",
        type: "MATERIAL",
        uom: "",
        quantity: 0,
        unitPrice: 0,
    }],
};

const createDefaultSection = (): section => ({
    ...DEFAULT_SECTION,
    sectionItems: DEFAULT_SECTION.sectionItems.map((item) => ({ ...item })),
});

const EstimateStep4 = ({
        onChange,
        data,
        mode,
        errorForm,
        onPreview
    }: EstimateStep4Props) => {
     const [formData, setFormData] = useState<{
        quotes : quote[],
        quoteProfitMargin : number,
        notes: string,
        titleStep4:string,
     }>(data);

    const [error, setError] = useState({
        companyDescription:'',
        gutter:'',
    });

    const [quantity,setQuantity] = useState(0);

    //const [tabs, setTabs] = useState<string[]>([])
    const [targetTab, setTargetTab] = useState(0);
    const [editingTabIndex, setEditingTabIndex] = useState<number | null>(null);

    const [mainTotal, setMainTotal] = useState(100);
    const [subTotal, setSubTotal] = useState(0);
    const [total, setTotal] = useState(0);

    const [material, setMaterial] = useState(0);
    const [labor, setLabor] = useState(0);
    const [cogs, setCogs] = useState(0);
    const [profit, setProfit] = useState(0);
    const [quoteTotal, setQuoteTotal] = useState(0);



    const handleUpdateFormData = (update:any) =>{
        let data = {...formData};
        data.quotes[targetTab] = {...data.quotes[targetTab], ...update }
        onChange(data);
        setFormData(data)
    }

    const removeQuote = (quoteIndex: number ) => {
        if(formData.quotes.length <=1 ) return;

        let data = {...formData};
        data.quotes = data.quotes.filter((v, i) => i != quoteIndex);

        if(targetTab == quoteIndex) setTargetTab(0)
        else if(targetTab > quoteIndex) setTargetTab(targetTab - 1)

       onChange(data);
       setFormData(data)

    }

    const handleUpdateData = (update:any, index:number) =>{
        let data = {...formData};
        data.quotes[targetTab].sections[index] = {...data.quotes[targetTab].sections[index], ...update};

        // data[key] = value;
        onChange(data);
        setFormData({
            ...formData,
            quotes:data.quotes
        })
    }

    const handleUpdateQuotes = (update:any, index:number) =>{
        let data = {...formData};
        data.quotes[index] = {...data.quotes[index], ...update};

        // data[key] = value;
        onChange(data);
        setFormData({
            ...formData,
            quotes:data.quotes
        })
    }

    const handleRemove = (index:number) => {
        let data = [...formData.quotes];
        data[targetTab].sections.splice(index, 1)
        setFormData({
            ...formData,
            quotes:data
        })
         onChange(data);
    }

    const handleUpdateTitle = (update:any) =>{

        let data = {...formData, ...update};
        setFormData(data);
        onChange(data); 
    }

    const handleSectionUpdateData = (update:any, indexQuote:number, indexSection:number) =>{
        let data = {...formData};
        let section = data.quotes[targetTab].sections[indexQuote];
        section.sectionItems[indexSection] = {...section.sectionItems[indexSection], ...update}

        onChange(data);
        setFormData({
            ...formData,
            quotes:data.quotes
        })
    }

    const handleAddQuote = () => {
        let data = {...formData};

        data.quotes[targetTab].sections.push({
            title: 'Section ' + (data.quotes[targetTab].sections.length + 1),
            isVisible: true,
            sectionItems:[
                {
                    itemName: '',
                    type:'MATERIAL',  // "MATERIAL",
                    uom: '',  //"SQ_FT",
                    quantity: 0,
                    unitPrice: 0
                }
            ]
        })
        let newValue = {
            ...formData,
            quotes:data.quotes
        }
        
        setFormData(newValue);
        onChange(data)
    }

    const handleAddSection = (index:number) => {
        let data = {...formData};
        data.quotes[targetTab].sections[index].sectionItems.push({
            itemName: '',
            type:'MATERIAL',  // "MATERIAL",
            uom: '',  //"SQ_FT",
            quantity: 0,
            unitPrice: 0
        })
        setFormData({
            ...formData,
            quotes:data.quotes
        })
        onChange(data)
    }

    const handleRemoveSection = (indexQuote:number, indexSection:number) => {
        let data = [...formData.quotes];
        data[targetTab].sections[indexQuote].sectionItems.splice(indexSection, 1)
        setFormData({
            ...formData,
            quotes:data
        })
    }

    const totalSection = (sections: sectionItem[]) => {
        let total = 0;
        sections.map((section) => {
            total += section.unitPrice * section.quantity
        })
        return formatMoney(total)
    }

    const addTab = () =>{
        let data = {...formData};
        const tabLength = data.quotes.length +1;
        data.quotes.push({
            quoteName:"Owens " + tabLength,
            sections:[createDefaultSection()],
            quoteProfitMargin: 0,
            note: "",
        })
        setTargetTab(data.quotes.length - 1);
        setFormData(data);
        onChange(data);
    }

    //drop - drag - section
    const handleDropSection = (newData:any[]) => {
        let data = {...formData};
        data.quotes[targetTab].sections = newData;
        setFormData(data);
        onChange(data)
    }

    //drop -drag - item
    const handleDropSectionItem = (newData:any[],indexQuote:number) => {
        let data = {...formData};
        data.quotes[targetTab].sections[indexQuote].sectionItems = newData;
        setFormData(data);
        onChange(data)
    }

    //Breakdown

    const updateBreakdown = () => {
        const currentQuote = formData.quotes?.[targetTab];
        if (!currentQuote) {
            setSubTotal(0);
            setTotal(0);
            setMaterial(0);
            setLabor(0);
            setCogs(0);
            setProfit(0);
            setQuoteTotal(0);
            return;
        }

        let progress = parseFloat(currentQuote.quoteProfitMargin ?? 0);

        // Allow profit margin from 0% to 99%
        if(progress < 0) progress = 0;
        if(progress > 0.99) progress = 0.99;

        let _mainTotal = 0;
        let _material = 0;
        let _labor = 0;

        // Calculate totals for current quote (targetTab) only
        (currentQuote.sections ?? []).forEach(section => {
            (section.sectionItems ?? []).forEach((sectionItem)=>{
                const itemTotal = (sectionItem.quantity * sectionItem.unitPrice);
                _mainTotal += itemTotal;

                // Calculate Material and Labor based on item type
                if(sectionItem.type === 'MATERIAL') {
                    _material += itemTotal;
                } else if(sectionItem.type === 'LABOR') {
                    _labor += itemTotal;
                }
            })
        })

        // Calculate breakdown values
        let _subTotal = _mainTotal;  // Sub Total = sum of all items
        let _profit = _subTotal * progress;  // Profit = Sub Total × Profit Margin
        let _total = _subTotal + _profit;  // Total = Sub Total + Profit
        let _cogs = _material + _labor;  // COGS = Material + Labor
        let _quoteTotal = _total;  // Quote Total = Total

        setSubTotal(_subTotal);
        setTotal(_total);

        setMaterial(_material);
        setLabor(_labor);
        setCogs(_cogs);
        setProfit(_profit);
        setQuoteTotal(_quoteTotal);
    }

    useEffect(()=>{
        let data = {...formData};
        if(!data.quotes || data.quotes.length == 0){
            data.quotes = [{
                quoteName:'',
                sections:[createDefaultSection()],
                quoteProfitMargin: 0,
                note:''
            }];
            setFormData(data);
            onChange(data);
        }
    },[])

    useEffect(() => {
        updateBreakdown();
    },[formData, targetTab])

    if(!formData.quotes || formData.quotes.length == 0 ) return (<></>)

    return (<div className={cx('estimate-step-container')}>
        <div className={cx('header','flex items-center justify-between')}>
            <TitleEditor value={formData.titleStep4} 
                name={""} 
                placeholder={""} 
                inputkey={""}
                isViewMode={mode == EstimateModeViewEnum.VIEW}
                onChange={(value)=>{
                    handleUpdateTitle({
                        titleStep4: value
                    })
                }}
                styleView={{
                    fontSize:"20px",
                    fontWeight: 700,
                }}
            />
            <div>
                    <button onClick={onPreview} className={cx('btn-preview')}>
                        View Page
                    </button>
                </div>
        </div>
       
        <div className={cx('content')}>
            <div className={cx('quotes')} >
                {
                    formData.quotes.map((quote, index)=><div className={cx({
                        selected: index == targetTab,
                        'quote-title':true,
                    })} onClick={(e)=>{
                        setTargetTab(index)
                        e.stopPropagation();
                    }}>
                        {editingTabIndex !== index && <button onClick={(e)=>{
                            removeQuote(index)
                            e.stopPropagation();
                        }}>
                            <svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M20 8L8 20M8 8L20 20" stroke="#FA6363" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>}
                        <TitleEditor value={quote.quoteName} 
                            name={""} 
                            placeholder={""} 
                            inputkey={""}
                            isViewMode={mode == EstimateModeViewEnum.VIEW}
                            onEditModeChange={(isEditing)=>{
                                setEditingTabIndex(isEditing ? index : null)
                            }}
                            onChange={(value)=>{
                            handleUpdateQuotes({
                                    quoteName: value
                                }, index)
                            }}
                            styleView={{
                                fontSize:"20px",
                                fontWeight: 700,
                            }}
                        />
                    </div>)
                }

                {
                    mode != EstimateModeViewEnum.VIEW && <div onClick={addTab}>
                        <PlusIcon color="#ffffff" />
                    </div>
                }
                
            </div>

            

            {
                formData.quotes[targetTab] ?
                <>
                {
                   
                    <DroppableArea data={formData.quotes[targetTab].sections} 
                        onChange={(newArray: any[]) => {
                            handleDropSection(newArray);
                        } } 
                        groupName={"sections"}
                        renderComponent={(data: any, index: number)=>{
                             return (
                                <BaseBoxContent className={cx('section-item')} expanded={true}>
                                    
                                    <div data-slot="header">
                                        <div className={cx('section-title')}>
                                            <div className={cx('drag-button')}>
                                                <DragIcon className={cx("drag-icon")} />
                                            </div>
                                            <div className="flex items-center justify-between w-full">
                                                <TitleEditor value={data.title} 
                                                    name={""} 
                                                    placeholder={""} 
                                                    inputkey={""}
                                                    isViewMode={mode == EstimateModeViewEnum.VIEW}
                                                    onChange={(value)=>{
                                                        handleUpdateData({ title:value }, index)
                                                    }} />

                                                <div className="flex items-center">

                                                    <button className="w-[24px] h-[24px] cursor-pointer mr-[32px]" onClick={()=>{
                                                         handleUpdateData({ isVisible: !data.isVisible}, index)
                                                    }}>
                                                        <EyeIcon stroke={data.isVisible ? "#ffffff" : "#8d8d8dff"} />
                                                    </button>

                                                    <button className="w-[24px] h-[24px] cursor-pointer" onClick={()=>{
                                                         handleRemove(index)
                                                    }}>
                                                        <Trash2  color="#FA6363" />
                                                    </button>
                                                </div>
     
                                            </div>
                                            
                                        </div>
                                    </div>
                                    <div data-slot="content">
                                        <div className={cx('section-item-table')}>
                                            <div className={cx('section-item-header')}>
                                                <div className="w-[24px]">
                                                </div>
                                                <div className="w-[28%]">
                                                    <h3>Item Name</h3>
                                                </div>
                                                <div className="w-[16%]">
                                                    <h3>UoM</h3>
                                                </div>
                                                <div className="w-[16%]">
                                                    <h3>Quantity</h3>
                                                </div>
                                                <div className="w-[16%]">
                                                    <h3>Price</h3>
                                                </div>
                                                <div className="w-[19%]">
                                                    <h3>Line Total</h3>
                                                </div>
                                                {
                                                    mode != EstimateModeViewEnum.VIEW && <div className="w-[5%]">
                                                    </div>
                                                }
                                                
                                            </div>
                                            <div className={cx('section-item-table-body')}>
                                                {
                                                    <DroppableArea data={data.sectionItems}
                                                        groupName={"sectionItems" + index}
                                                        onChange={(sectionItems)=>{
                                                            handleDropSectionItem(sectionItems, index);
                                                        }}
                                                        renderComponent={(section:sectionItem, indexSection:any)=>{
                                                            let itemError:any= {};
                                                            if(errorForm && errorForm.quotes && errorForm.quotes.quotesList){
                                                                if(errorForm.quotes.quotesList[index] && errorForm.quotes.quotesList[index].sectionList){
                                                                    if(errorForm.quotes.quotesList[index].sectionList[indexSection]){
                                                                        itemError = errorForm.quotes.quotesList[index].sectionList[indexSection]
                                                                    }
                                                                }
                                                            }

                                                            return (
                                                                <div className={cx('section-item-table-row')}                                                 >
                                                                    <div className={cx('drag-button')}>
                                                                        <DragIcon className={cx("drag-icon")} />
                                                                    </div>
                                                                    <div className="w-[28%]">
                                                                        <FormInput 
                                                                            name={"itemName"} 
                                                                            text={""} 
                                                                            isViewMode={mode == EstimateModeViewEnum.VIEW}
                                                                            isViewNoInput={true}
                                                                            placeholder={""} 
                                                                            inputkey={"itemName"}
                                                                            value={section.itemName}
                                                                            error={
                                                                                itemError.itemName ? itemError.itemName : ""
                                                                            }
                                                                            onChange={(e)=>{
                                                                                handleSectionUpdateData({
                                                                                    itemName:e.target.value
                                                                                },
                                                                                index,
                                                                                indexSection)
                                                                            }}
                                                                            height="31px"
                                                                        />
                                                                    </div>
                                                                    <div className="w-[16%]">
                                                                        <BaseSelectCustom
                                                                            selectedView={section.uom}
                                                                            onViewChange={(viewId) => {
                                                                            //  setCurrentFilter(viewId);
                                                                                handleSectionUpdateData({
                                                                                    uom:viewId,
                                                                                },
                                                                                index,
                                                                                indexSection)
                                                                            }}
                                                                            height="36px"
                                                                            isViewMode={mode == EstimateModeViewEnum.VIEW}
                                                                            isViewNoInput={true}
                                                                            error={
                                                                                itemError.uom ? itemError.uom : ""
                                                                            }
                                                                            viewOptions={EstimateUoMOption}
                                                                            placeholder="---"
                                                                            className={cx({
                                                                                'select-filter':true,
                                                                                'view-mode': mode == EstimateModeViewEnum.VIEW
                                                                            })}
                                                                            minWidth={'100px'}
                                                                            textAlign="left"
                                                                            IconDrop={
                                                                                <div className={cx('drop')}>
                                                                                <svg width="24" height="24" viewBox="0 0 24 24"       fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                        <path d="M11.9994 14.308L8.19141 10.5H15.8074L11.9994 14.308Z" fill="black"/>
                                                                                    </svg>
                                                                                </div>
                                                                            }
                                                                        />
                                                                    </div>
                                                                    <div className="w-[16%]">
                                                                        <FormInput
                                                                            name={"number"}
                                                                            text={""}
                                                                            value={section.quantity}
                                                                            // minValue={0}
                                                                            height="31px"
                                                                            isViewMode={mode == EstimateModeViewEnum.VIEW}
                                                                            isViewNoInput={true}
                                                                            error={itemError.quantity ? itemError.quantity : ""}
                                                                            onChange={(e:any) => {
                                                                                let val = parseFloat(e.target.value);
                                                                                if (!isNaN(val) && val > MAX_QUANTITY) val = MAX_QUANTITY;
                                                                                handleSectionUpdateData({
                                                                                    quantity: isNaN(val) ? e.target.value : val
                                                                                },
                                                                                    index,
                                                                                    indexSection);
                                                                            } }
                                                                            inputkey={""} 
                                                                            placeholder={""}                                                                        />
                                                                    </div>
                                                                    <div className="w-[16%]">
                                                                        <div className="w-full">
                                                                            <FormInput 
                                                                                name={"company"} 
                                                                                text={""} 
                                                                                isViewMode={mode == EstimateModeViewEnum.VIEW}
                                                                                isViewNoInput={true}
                                                                                placeholder={""} 
                                                                                inputkey={"company"}
                                                                                value={section.unitPrice}
                                                                                error={
                                                                                    itemError.quantity ? itemError.unitPrice : ""
                                                                                }
                                                                                onChange={(e)=>{
                                                                                    let val = parseFloat(e.target.value);
                                                                                    if (!isNaN(val) && val > MAX_UNIT_PRICE) val = MAX_UNIT_PRICE;
                                                                                    handleSectionUpdateData({
                                                                                        unitPrice: isNaN(val) ? e.target.value : val
                                                                                    },
                                                                                    index,
                                                                                    indexSection)
                                                                                }}
                                                                                height="31px"
                                                                            />
                                                                        </div>
                                                                        
                                                                    </div>
                                                                    <div className="w-[19%]">
                                                                        <div className={cx('total-money')}>
                                                                            <h3>${formatMoney(section.unitPrice * section.quantity)}</h3>
                                                                        </div>
                                                                        
                                                                    </div>
                                                                    {
                                                                        mode != EstimateModeViewEnum.VIEW && <div className="w-[5%] flex items-center">
                                                                            <div className={cx('text-delete')} onClick={()=>handleRemoveSection(index, indexSection)}>
                                                                               <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                                    <path d="M3.33398 3.33398L12.6673 12.6673" stroke="#FA6363" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                                                    <path d="M3.33269 12.6673L12.666 3.33398" stroke="#FA6363" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                                                                                </svg>

                                                                            </div>
                                                                        </div>
                                                                    }
                                                                </div>
                                                            )
                                                        }}
                                                    />
                                                    
                                                }
                                                
                                            </div>
                                           
                                            
                                        </div>
                                    </div>

                                    <div data-slot="footer">
                                        {
                                            mode != EstimateModeViewEnum.VIEW && <div className={cx('section-action')}>
                                                <AddItem onClick={()=>{handleAddSection(index)}} />
                                            </div>
                                        }
                                        <div className={cx('section-item-footer', {
                                            "mt-[20px]":  mode == EstimateModeViewEnum.VIEW
                                        })}>
                                            <div>
                                                {/* {
                                                    errorForm.quotes && 
                                                    errorForm.quotes.quotesList &&
                                                    errorForm.quotes.quotesList[index] &&
                                                    errorForm.quotes.quotesList[index].sections && <p className={cx("error-message")}>{errorForm.quotes.quotesList[index].sections}</p>
                                                } */}
                                            </div>
                                            <div className={cx('total')}>
                                                <h4>Section Total</h4>
                                                <h3>${totalSection(data.sectionItems)}</h3>
                                            </div>
                                        </div>
                                    </div>
                                </BaseBoxContent>      
                            )
                        } } />
                }
                    </>
                : <></>
            }

            

            {
                mode != EstimateModeViewEnum.VIEW &&
                <div className=" flex items-center justify-between">
                    <Divider className="w-[calc(50%-150px)] !bg-[#1A2B47] !border-b-[#1A2B47]" />
                    <button className={cx('add-section')} onClick={()=>{handleAddQuote()}}>
                        Add Section
                    </button>
                    <Divider className="w-[calc(50%-150px)] !bg-[#1A2B47] !border-b-[#1A2B47]" />
                </div>
                
                
            }

            {/* {
                errorForm.quotes  && errorForm.quotes.sections && <p className={cx("error-message")}>{errorForm.quotes.sections}</p>
            }

             */}

            <div className={cx('section-item','mt-[40px]')}>
                <div className={cx('section-title')}>
                    <p>Estimate Summary</p>
                </div>

                <div >
                    <div  className="flex items-center justify-between gap-[70px] mt-[17px] ">
                        <div className="w-[30%]">
                            <FormInput 
                                name={"company"} 
                                text={"Profit Margins %"} 
                                placeholder={""} 
                                inputkey={"company"}
                                isViewMode={mode == EstimateModeViewEnum.VIEW}
                                value={ Number(((Number(formData.quotes?.[targetTab]?.quoteProfitMargin) || 0) * 100).toFixed(0))}
                                error={errorForm.quoteProfitMargin}
                                onChange={(e)=>{
                                    let value = parseFloat(e.target.value);

                                    if(!isNaN(value)) {
                                        if(value < 0) value = 0;
                                        if(value > 99) value = 99;
                                    }

                                    handleUpdateFormData({
                                        quoteProfitMargin: !isNaN(value) ? value/100 : 0
                                    })
                                }}
                            />
                        </div>

                        <div className={cx('summary-total')}>
                            <div className={cx('summary-item')}>
                                <p className="!font-normal">Estimate Sub total</p>
                                <p className="w-[150px]">$ {formatMoney(subTotal)}</p>
                            </div>

                            <Divider className='my-[8px] !bg-[#1A2B47]' />

                            <div className={cx('summary-item', "bg-[#0E264C]")}>
                                
                                <p>Total</p>
                                <p className="w-[150px] !text-[#2D80FC]">$ {formatMoney(total)}</p>
                            </div>
                        </div>
                    </div>

                    <div className={cx('total-breakdown','mt-[17px]')}>
                        <div className={cx("breakdown-title")}>
                            <h2>Total Breakdown</h2>

                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M17 11L12 6L7 11M17 18L12 13L7 18" stroke="#F9FBFE" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>

                        </div>
                            
                        <div className={cx("breakdown-content","mt-[5px]")}>
                            <div className={cx("breakdown-item")}>
                                <h4>Material</h4>
                                <h2>${formatMoney(material)}</h2>
                            </div>

                            <div className={cx("breakdown-item")}>
                                <h4>Labor</h4>
                                <h2>${formatMoney(labor)}</h2>
                            </div>

                            <div className={cx("breakdown-item")}>
                                <h4>COGS</h4>
                                <h2>${formatMoney(cogs)}</h2>
                            </div>

                            <div className={cx("breakdown-item")}>
                                <h4>Profit</h4>
                                <h2>${formatMoney(profit)}</h2>
                            </div>

                            <div className={cx("breakdown-item")} >
                                <h4>Quote Total</h4>
                                <h2 style={{
                                    color: "#2D80FC"
                                }}>${formatMoney(quoteTotal)}</h2>
                            </div>
                        </div>
                    </div>

                    <div className="mt-[34px]">
                        <FormInput 
                            name={"company"} 
                            text={"Add Note (Optional)"} 
                            placeholder={""} 
                            inputkey={"company"}
                            value={formData.quotes[targetTab].note}
                            isViewMode={mode == EstimateModeViewEnum.VIEW}
                            inputType="textarea"
                            error={errorForm.quoteNote}
                            height="85px"
                            onChange={(e)=>{
                                handleUpdateFormData({
                                    note:e.target.value
                                })
                            }}
                        />
                    </div>
                </div>

            </div>
        </div>
    </div>)
}

export default EstimateStep4;